import { useSyncExternalStore } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FechaHora } from '@/ui/Numero'
import { sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import { useAuditoriaDespacho } from '@/dominio/despacho/useDespacho'
import { DetalleJson } from '../consola/-comun'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDelDespacho } from './-comun'

/**
 * La auditoría del despacho: quién del equipo invitó, aceptó, asignó, entró a
 * qué cliente y cuándo. Solo la ven los titulares.
 *
 * Lo que toca a un cliente está también en la auditoría de ese cliente, que es
 * la que él ve. Un contribuyente se nombra aquí solo si tuvo relación con el
 * despacho: de una invitación rechazada se sabe el RNC y nada más.
 */
export const Route = createFileRoute('/despacho/auditoria')({
  component: PantallaAuditoriaDespacho,
})

const NOMBRES: Record<string, string> = {
  'despacho.registrar': 'Registró el despacho',
  'despacho.crear': 'Agentical Group dio de alta el despacho',
  'despacho.perfil': 'Cambió el perfil',
  'despacho.equipo.agregar': 'Agregó a alguien al equipo',
  'despacho.equipo.rol': 'Cambió un rol',
  'despacho.equipo.retirar': 'Retiró a alguien del equipo',
  'despacho.invitacion.enviar': 'Invitó a un cliente',
  'despacho.invitacion.retirar': 'Retiró una invitación',
  'despacho.invitacion.cliente': 'Un cliente invitó al despacho',
  'despacho.vinculo.aceptar': 'Vínculo aceptado',
  'despacho.vinculo.rechazar': 'Vínculo rechazado',
  'despacho.vinculo.revocar': 'Vínculo revocado',
  'despacho.asignacion': 'Cambió quién ve a un cliente',
  'despacho.entrada': 'Entró a un cliente',
  'despacho.estado': 'Agentical Group cambió el estado del despacho',
  'despacho.verificar': 'Agentical Group verificó el despacho',
  'despacho.cobro.tarifa': 'Agentical Group fijó la tarifa',
  'despacho.cobro.pago': 'Agentical Group registró un pago',
  'despacho.cobro.anular': 'Agentical Group anuló un cargo',
}

function PantallaAuditoriaDespacho() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const esTitular = sesion?.despacho?.rol === 'titular'
  const consulta = useAuditoriaDespacho(esTitular)

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla titulo="Auditoría" descripcion="Lo que hizo el despacho, del más reciente al más antiguo." />

      {!esTitular ? (
        <EstadoVacio variante="sin-resultados" mensaje="La auditoría del despacho la ven los titulares." />
      ) : (
        <Tarjeta relleno={false}>
          {consulta.isError ? (
            <ErrorDelDespacho error={consulta.error} queCargaba="la auditoría" onReintentar={() => void consulta.refetch()} />
          ) : !consulta.isLoading && (consulta.data ?? []).length === 0 ? (
            <EstadoVacio variante="resuelto" mensaje="Todavía no hay nada anotado." />
          ) : (
            <table className="w-full border-collapse text-cuerpo">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300 text-left text-etiqueta font-semibold text-gris-600">
                  <th scope="col" className="w-[170px] px-3 py-2">Cuándo</th>
                  <th scope="col" className="px-3 py-2">Qué</th>
                  <th scope="col" className="px-3 py-2">Quién</th>
                  <th scope="col" className="px-3 py-2">Cliente</th>
                  <th scope="col" className="px-3 py-2">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading ? (
                  <FilasEsqueleto filas={6} columnas={5} />
                ) : (
                  (consulta.data ?? []).map((e) => (
                    <tr key={e.id} className="border-b border-gris-200 align-top last:border-b-0">
                      <td className="px-3 py-2">
                        <FechaHora valor={e.ocurridoEl} />
                      </td>
                      <td className="px-3 py-2 text-gris-900">{NOMBRES[e.accion] ?? e.accion}</td>
                      <td className="px-3 py-2 text-gris-700">{e.operador ? `Agentical Group (${e.email ?? '—'})` : (e.email ?? '—')}</td>
                      <td className="px-3 py-2 text-gris-700">{e.razonSocial ?? '—'}</td>
                      <td className="max-w-[320px] px-3 py-2">
                        <DetalleJson valor={e.detalle} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Tarjeta>
      )}
    </div>
  )
}
